/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { of, Subject } from 'rxjs';
import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, AfterViewInit, ElementRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DistributorSupportService } from '../services/distributorSupport.service';
import { GenealogyService } from '../services/genealogy.service';
import { Router } from '@angular/router';
import { debounceTime, delay, flatMap } from 'rxjs/operators';
import { CommonService } from '../../shared/services/common.service';

@Component({
  selector: 'app-distributor-search',
  templateUrl: './distributor-search.component.html',
  styleUrls: ['./distributor-search.component.scss']
})

export class DistributorSearchComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Output() memberSelected: EventEmitter<any> = new EventEmitter();
  @Output() clearSelectedFilter: EventEmitter<any> = new EventEmitter();
  @Output() closeSearchComponent: EventEmitter<any> = new EventEmitter();
  @Input() searchHeader: any;
  @Input() showSearchBar = true;
  @Input() displayPlanName = true;
  @Input() disabledField: any = false;
  @Input() showSelectedMember = true;
  @Input() navigateToDLPage = false;
  @Input() showTypeAhead = true;
  @Input() defaultFilterValue: any;
  @Input('clientId') participantId: any;
  @Input() isFrom: any;
  @Input() periodName: any;
  @Input() selectedMember: any = null;
  @Input() noDistributorMsg = '';
  @Input() isTLSearch: boolean = false;
  public globalFilterValue = '';
  public distributorData: any = [];
  public filterBy: any = { group: '3', isHomeMarket: false };
  public downlineParticipants: any = [];
  private searchTerms = new Subject<any>();
  public isDataFiltered = false;

  constructor(
    private router: Router,
    private distributorSupportService: DistributorSupportService,
    private genealogyService: GenealogyService,
    private toastr: ToastrService,
    public commonService: CommonService,
    public elementRef:ElementRef
  ) {}

  ngOnInit() {
    if (this.participantId) {
      this.getDownlineMembers();
    }
    this.searchTerms
      .pipe(
        debounceTime(500),
        flatMap((search) => of(search).pipe(delay(100)))
      )
      .subscribe(this.filterData.bind(this));
  }

  ngAfterViewInit(): void {
    const globalSearchInput = document.getElementById('globalSearchInputTag');
    if (globalSearchInput) {
      globalSearchInput.focus();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.defaultFilterValue && changes.defaultFilterValue.currentValue) {
      this.globalFilterValue = changes.defaultFilterValue.currentValue;
      this.globalFilterValue = this.globalFilterValue.trim();
      if (!this.globalFilterValue) {
        this.clearFilter();
      }
    }
  }

  public getTranslationWithKey(key: string) {
    return this.commonService.getTranslatedVal(key);
  }

  public search(term: string): void {
    this.isDataFiltered = false;
    if (term) {
      this.searchTerms.next(term);
    } else {
      this.clearFilter();
    }
  }

  public filterData() {
    if (this.participantId) {
      this.distributorData = [];
      let searchText = this.globalFilterValue || '',
        clientId,
        name;
      searchText = searchText.toLowerCase().trim();
      this.downlineParticipants.forEach((element: any) => {
        clientId = element?.clientId.toLowerCase();
        name = element.name.toLowerCase();
        if (searchText) {
          if (clientId.includes(searchText) || name.includes(searchText)) {
            this.distributorData.push(element);
          }
        }
      });
      this.isDataFiltered = true;
      if (!this.distributorData.length) {
        this.memberSelected.emit(null);
      }
    } else {
      if (this.globalFilterValue.length >= 1) {
        if (this.showTypeAhead) {
          this.getAllMembers();
        } else {
          this.commonService.publishData(this.globalFilterValue);
        }
      } else if (!this.globalFilterValue) {
        this.clearFilter();
      }
    }
  }

  private getAllMembers() {
    let searchText = this.globalFilterValue || '', hideLoader;
    if (this.isFrom === 'sidebar') {
      hideLoader = true;
    } else {
      hideLoader = false;
    }
    searchText = searchText.trim();
    if (searchText) {
      if (this.isTLSearch) {
        this.distributorSupportService
          .searchTLs(searchText, false, undefined, undefined, hideLoader, this.periodName)
          .then(this.getMembersSH.bind(this),(reason) => this.commonService.commonErrorHandler(reason))
      } else {
        this.distributorSupportService
          .filterGloballyByEnteredValue(searchText, false, undefined, undefined, hideLoader, this.periodName)
          .then(this.getMembersSH.bind(this),(reason) => this.commonService.commonErrorHandler(reason))
      }
    }
  }

  private abbrName(strings: any, fNameExp: string, lNameExp: string) {
    let str = '';
    if (fNameExp) {
      str += fNameExp.substring(0, 1);
    }
    if (lNameExp) {
      str += lNameExp.substring(0, 1);
    }

    return str.toUpperCase();
  }

  public getAbbrName(selectedMember: any) {
    const str = this.abbrName`${selectedMember.firstName}${selectedMember.lastName || ''}`; /* Template Strings */
    return str;
  }

  private getMembersSH(data: any) {
    const membersList = data ? data.data : [],
      finalMembersList: any = [];
    if (membersList && membersList.length) {
      membersList.forEach((item: any) => {
        const memberobj: any = item;
        memberobj.name = (item.firstName || '') + ' ' + (item.lastName || '');
        finalMembersList.push(memberobj);
      });
    }
    this.distributorData = finalMembersList;
    this.isDataFiltered = true;
    this.distributorSupportService.setDistributorData(this.distributorData);
  }

  private getDownlineMembers() {
    this.distributorData = [];
    const selectedGroup = this.filterBy.group || '3',
      homeMarket = this.filterBy.isHomeMarket ? 'homemarket' : 'all';
    this.genealogyService
      .getGroupDetails(this.participantId, selectedGroup, homeMarket)
      .then(this.getDownlinesSH.bind(this), this.getDownlinesFH.bind(this));
  }

  private getDownlinesSH(data: any) {
    this.downlineParticipants = [];
    const membersList = data ? data : [];
    if (membersList && membersList.length) {
      membersList.forEach((item: any) => {
        const memberobj: any = item;
        memberobj.name = (item.firstName || '') + ' ' + (item.lastName || '');
        this.downlineParticipants.push(memberobj);
      });
    }
    // this.distributorSupportService.setDistributorData(this.distributorData);
  }

  private getDownlinesFH(err: any) {
    const error = JSON.parse(err);
    let altLbl = '';
    if (error.errors && error.errors.message) {
      altLbl = error.errors.message;
    } else if (error.message) {
      altLbl = error.message;
    } else {
      altLbl = 'Unknown Problem';
    }
    this.toastr.error(altLbl, this.getTranslationWithKey('error'),{ enableHtml: true });
  }

  public clearFilter() {
    if (!this.disabledField) {
      this.globalFilterValue = '';
      this.distributorData = '';
      this.selectedMember = null;
      this.isDataFiltered = false;
      if (this.clearSelectedFilter) {
        this.clearSelectedFilter.emit();
      }
    }
  }

  public selectSpecificDistribuor(member: any) {
    this.selectedMember = member;
    // fix to populate client ID of selected member
    this.globalFilterValue = member?.clientId;
    this.distributorData = [];
    this.isDataFiltered = false;
    if (this.navigateToDLPage) {
      this.redirectToDLPage(member?.clientId);
    }
    if (this.memberSelected) {
      this.memberSelected.emit(member);
    }
  }

  public redirectToDLPage(clientId: any) {
    localStorage.setItem('clientIdFromHomePage', clientId);
    this.commonService.publishData(clientId);
    this.router.navigateByUrl('/fieldsupport/distributorList/default');
  }

  public closeSearch() {
    this.closeSearchComponent.emit(false);
  }

  ngOnDestroy() {
    this.globalFilterValue = '';
    this.searchTerms.unsubscribe();
  }
}
